import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Link from '@mui/material/Link';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';
import { BillingRecord } from '../../types';

const BillingPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { managementToken } = useManagementAuth();
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const perPage = 10;

  // ダウンロードメニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!managementToken || !companyId) return;
    setLoading(true);
    try {
      const [recordsData, countData] = await Promise.all([
        api.managementCompany.getBillingRecords(companyId, managementToken, page, perPage),
        api.managementCompany.getBillingCount(companyId, managementToken),
      ]);
      setRecords(recordsData);
      setTotalCount(countData.total);
    } catch (error) {
      console.error('利用明細の取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  }, [managementToken, companyId, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>, recordId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecordId(recordId);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
    setSelectedRecordId(null);
  };

  const handleDownload = async (downloadType: string) => {
    if (!managementToken || !companyId || !selectedRecordId) return;
    try {
      const blob = await api.managementCompany.downloadBillingCsv(
        companyId, selectedRecordId, downloadType, managementToken
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing_${downloadType}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('ダウンロードに失敗しました', error);
    }
    handleDownloadClose();
  };

  // 請求額調整セルのクリック: ダウンロード可能な場合のみ請求額調整明細をCSV出力
  const handleAdjustmentClick = async (recordId: string) => {
    if (!managementToken || !companyId) return;
    try {
      const blob = await api.managementCompany.downloadBillingCsv(
        companyId, recordId, 'adjustment', managementToken
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing_adjustment_${recordId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('請求額調整明細のダウンロードに失敗しました', error);
    }
  };

  const formatPeriod = (record: BillingRecord) => {
    if (!record.is_confirmed) {
      return '未確定(月末確定予定)';
    }
    return `${record.billing_year}年${record.billing_month}月1日~${record.billing_year}年${record.billing_month}月${new Date(record.billing_year, record.billing_month, 0).getDate()}日`;
  };

  const formatAmount = (amount: number) => {
    return `${amount.toLocaleString()}円`;
  };

  const totalPages = Math.ceil(totalCount / perPage);

  if (loading && records.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
        利用明細
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        いままでの利用明細の確認やCSVファイルのダウンロードができます。
      </Typography>

      {/* ページネーション（上部） */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {(page - 1) * perPage + 1}~{Math.min(page * perPage, totalCount)}件表示中 / {totalCount}件中
          </Typography>
        </Box>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>期間</TableCell>
                  <TableCell align="center">対象店舗数</TableCell>
                  <TableCell align="center">対象人数</TableCell>
                  <TableCell align="center">対象求人数</TableCell>
                  <TableCell align="center">振込関連手数料</TableCell>
                  <TableCell align="center">請求額調整</TableCell>
                  <TableCell align="center">システム利用料</TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography variant="caption" display="block">請求額の目安</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        正確な金額は請求書のPDFをご確認ください
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell>
                      <Link
                        component="button"
                        onClick={() => navigate(`/management/company/${companyId}/billing/${record.id}`)}
                        sx={{ textDecoration: 'none', cursor: 'pointer' }}
                      >
                        {formatPeriod(record)}
                      </Link>
                    </TableCell>
                    <TableCell align="center">{record.target_store_count}店舗</TableCell>
                    <TableCell align="center">{record.target_worker_count}人</TableCell>
                    <TableCell align="center">{record.target_job_count}件</TableCell>
                    <TableCell align="center">{formatAmount(record.transfer_fee)}</TableCell>
                    <TableCell align="center">
                      {record.adjustment_downloadable ? (
                        <Link
                          component="button"
                          onClick={() => handleAdjustmentClick(record.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          {formatAmount(record.billing_adjustment)}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary" component="span">
                          {formatAmount(record.billing_adjustment)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{formatAmount(record.system_usage_fee)}</TableCell>
                    <TableCell align="center">{formatAmount(record.total_amount)}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<KeyboardArrowDownIcon />}
                        onClick={(e) => handleDownloadClick(e, record.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        ダウンロード
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">利用明細がありません</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ダウンロードメニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDownloadClose}
      >
        <MenuItem onClick={() => handleDownload('employment')}>
          利用明細（就業別）
        </MenuItem>
        <MenuItem onClick={() => handleDownload('store')}>
          利用明細（店舗別）
        </MenuItem>
        <MenuItem onClick={() => handleDownload('transfer_fee')}>
          振込関連手数料明細
        </MenuItem>
        <MenuItem onClick={() => handleDownload('adjustment')}>
          請求額調整明細
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BillingPage;
